import { capitalizeFirstLetter } from "../../../utils";

/** How many Stripe invoices to load for the billing history section. */
export const BILLING_INVOICE_FETCH_LIMIT = 36;

/**
 * @param {string} collectionMethod
 * @returns {boolean}
 */
export const isManualBillingCollection = (collectionMethod) =>
  collectionMethod === "manual_invoice";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * @param {number|string|Date|null|undefined} value Unix seconds, ms, or Date
 * @returns {Date|null}
 */
export const toBillingDate = (value) => {
  if (value == null) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }

  const ms = numeric > 1e12 ? numeric : numeric * 1000;
  const date = new Date(ms);
  return Number.isFinite(date.getTime()) ? date : null;
};

/**
 * @param {number|string|Date|null|undefined} value
 * @returns {string}
 */
export const formatBillingDate = (value) => {
  const date = toBillingDate(value);
  if (!date) {
    return "—";
  }

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * @param {number|null|undefined} cents
 * @returns {string}
 */
export const formatMoneyFromCents = (cents) => {
  if (typeof cents !== "number" || !Number.isFinite(cents)) {
    return "—";
  }

  return `$${(cents / 100).toFixed(2)}`;
};

/**
 * Returns the face value of an invoice in cents for display purposes.
 *
 * Prefers `total` (the invoice amount regardless of payment status) over
 * `amount_paid` so that open/unpaid invoices show the correct amount due
 * rather than $0.00.
 *
 * @param {object} invoice Stripe or manual invoice payload
 * @returns {number|null}
 */
export const getInvoiceAmountCents = (invoice) => {
  if (typeof invoice?.total === "number") {
    return invoice.total;
  }

  if (typeof invoice?.amount_paid === "number" && invoice.amount_paid > 0) {
    return invoice.amount_paid;
  }

  return null;
};

/**
 * @param {object} invoice Stripe invoice payload
 * @returns {string}
 */
export const getInvoiceDescription = (invoice) => {
  if (invoice?.description) {
    return invoice.description;
  }

  if (invoice?.source === "manual" && invoice?.billingPeriodStart) {
    const start = formatBillingDate(invoice.billingPeriodStart);
    const end = formatBillingDate(invoice.billingPeriodEnd);
    return `Subscription · ${start} – ${end}`;
  }

  const lineDescription = invoice?.lines?.data?.[0]?.description;
  if (lineDescription) {
    return lineDescription;
  }

  return "Subscription";
};

/**
 * @param {object} invoice Stripe invoice payload
 * @returns {number}
 */
export const getInvoiceTimestamp = (invoice) => {
  const date =
    toBillingDate(invoice?.created) ||
    toBillingDate(invoice?.period_end) ||
    toBillingDate(invoice?.status_transitions?.paid_at);

  return date ? date.getTime() : 0;
};

/**
 * Groups Stripe invoices by calendar year and month for customer-friendly history.
 *
 * @param {Array<object>} invoices
 * @returns {Array<{ year: number, months: Array<{ month: number, monthLabel: string, invoices: Array<object> }> }>}
 */
export const groupInvoicesByYearAndMonth = (invoices = []) => {
  const sorted = [...invoices].sort(
    (left, right) => getInvoiceTimestamp(right) - getInvoiceTimestamp(left)
  );
  const yearsMap = new Map();

  sorted.forEach((invoice) => {
    const date = toBillingDate(invoice?.created) || toBillingDate(invoice?.period_end);
    if (!date) {
      return;
    }

    const year = date.getFullYear();
    const month = date.getMonth();

    if (!yearsMap.has(year)) {
      yearsMap.set(year, new Map());
    }

    const monthsMap = yearsMap.get(year);
    if (!monthsMap.has(month)) {
      monthsMap.set(month, []);
    }

    monthsMap.get(month).push(invoice);
  });

  return Array.from(yearsMap.entries())
    .sort((left, right) => right[0] - left[0])
    .map(([year, monthsMap]) => ({
      year,
      months: Array.from(monthsMap.entries())
        .sort((left, right) => right[0] - left[0])
        .map(([month, monthInvoices]) => ({
          month,
          monthLabel: MONTH_NAMES[month] || `Month ${month + 1}`,
          invoices: monthInvoices,
        })),
    }));
};

/**
 * @param {string} value
 * @returns {string}
 */
export const formatLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => capitalizeFirstLetter(part))
    .join(" ");

/**
 * Customer-facing billing status for Settings > Billing.
 *
 * @param {{ billingAccount?: object, collectionMethod?: string, requiresBillingAttention?: boolean }} params
 */
export const getCustomerFacingBillingStatus = ({
  billingAccount = {},
  collectionMethod = "manual_invoice",
  requiresBillingAttention = false,
}) => {
  if (collectionMethod === "manual_invoice") {
    return {
      label: "Contract billing",
      badgeColor: "blue",
      description:
        "Your organization is on contract billing. Invoices created for your account appear in the history below.",
    };
  }

  const status = billingAccount?.status;

  if (requiresBillingAttention || status === "past_due") {
    return {
      label: "Payment needed",
      badgeColor: "red",
      description:
        "We could not collect your latest payment. Update your payment method to avoid interruption.",
    };
  }

  if (status === "active") {
    return {
      label: "Active",
      badgeColor: "green",
      description: "Your subscription is active. Download past invoices below or manage billing in Stripe.",
    };
  }

  if (status === "canceled") {
    return {
      label: "Canceled",
      badgeColor: "gray",
      description: "This subscription is no longer active. Choose a plan below to subscribe again.",
    };
  }

  return {
    label: "Setup needed",
    badgeColor: "amber",
    description:
      "Choose a plan to start your subscription and unlock hosted monthly invoices here.",
  };
};

/**
 * @param {string} interval month | year
 * @returns {string}
 */
export const formatBillingCycleLabel = (interval) => {
  if (interval === "year") {
    return "Yearly";
  }

  if (interval === "month") {
    return "Monthly";
  }

  return interval ? capitalizeFirstLetter(interval) : "Monthly";
};

/**
 * @param {string} status Stripe invoice status
 * @returns {{ label: string, color: string }}
 */
export const getInvoiceStatusDisplay = (status) => {
  switch (status) {
    case "paid":
      return { label: "Paid", color: "green" };
    case "open":
      return { label: "Due", color: "amber" };
    case "void":
      return { label: "Void", color: "gray" };
    case "uncollectible":
      return { label: "Uncollectible", color: "red" };
    case "draft":
      return { label: "Draft", color: "gray" };
    default:
      return { label: formatLabel(status || "unknown"), color: "gray" };
  }
};
